<template>
    <div class="flex-1">
        <Header :count="count" />

        <div class="account__container pt-none">
            <div class="account__content account__content--small">
                <p class="lead mt-xl mb-xxl">
                    My Jobs allows you to keep track of your solar PV installations. Before adding a new job, add your <a href="/account/products" class="link">favourite products</a>.
                    For assistance download the <a class="link" target="_blank" href="https://assets.cleanenergycouncil.org.au/documents/member-and-installer-only/my-jobs/My-jobs-self-help.pdf">self-help guide.</a>
                </p>
            </div>

            <div class="search-filter my-none mb-xl">
                <div class="search-filter__section-top">
                    <h1 class="h3 search-filter__heading">Search</h1>
                    <form class="search-filter__input-wrap no-setup" @submit.prevent="fetchFilteredSet({ q: query })">
                        <input v-model="query" class="search-filter__search-input" type="text" placeholder="Search">
                        <button>
                            <svg class="icon search-filter__search-icon">
                                <use xlink:href="#search" />
                            </svg>
                        </button>
                    </form>

                    <a class="iconlink search-filter__toggle" @click="showFilters = !showFilters">
                        <svg class="icon ">
                            <use xlink:href="#filter-bars" />
                        </svg>
                    </a>
                </div>

                <div v-if="showFilters" class="search-filter__section-bottom search-filter__section-bottom--active">
                    <div class="search-filter__checkbox-grid">
                        <h5 class="h6 search-filter__grid-heading">Status</h5>
                        <v-select
                            v-model="status"
                            class="account-select"
                            :clearable="false"
                            :searchable="false"
                            :options="[{ label: 'All', value: null }, { label: 'Complete', value: 'complete' }, { label: 'Incomplete', value: 'incomplete' }]"
                            @input="fetchFilteredSet({ status: $event.value })"
                        />
                    </div>

                    <div v-if="showDateFilter" class="search-filter__range">
                        <div class="search-filter__range-data">
                            <h5 class="h6 search-filter__range-heading">Date created</h5>
                            <p class="search-filter__date-value">{{ (dates.filterStart || dates.rangeStart) | date }} — {{ (dates.filterEnd || dates.rangeEnd) | date }}</p>
                        </div>
                        <div class="search-filter__slider-wrap">
                            <DateRange
                                v-if="dates.rangeStart && dates.rangeEnd"
                                :range-start="dates.rangeStart"
                                :range-end="dates.rangeEnd"
                                :filter-start.sync="dates.filterStart"
                                :filter-end.sync="dates.filterEnd"
                                @updated="fetchFilteredSet({ dateStart: $event.start, dateEnd: $event.end }, true)"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <template v-if="jobs.length">
                <JobItem v-for="job in jobs" :key="job.uid" :job="job" />
                <Pagination v-if="page.total > 1" :number-of-pages="page.total" :current-page="page.current" @change-page="changePage" />
            </template>

            <template v-else-if="!loading">
                <p class="no-results">
                    No jobs found for the current search. Please try again.
                </p>
            </template>
        </div>
        <NewJobModal ref="modal" />
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import moment from 'moment';
import Header from '@/jobs/list/Header';
import JobItem from '@/jobs/list/JobItem';
import DateRange from '@/jobs/list/DateRange';
import Pagination from '@/components/pagination-nav';
import NewJobModal from '@/jobs/new/Modal';
import { EventBus } from '@/event-bus';

export default {
    components: {
        Header,
        JobItem,
        DateRange,
        Pagination,
        NewJobModal
    },

    filters: {
        date(value) {
            return moment(value, 'YYYY-MM-DD').format('MMM YYYY');
        }
    },

    data() {
        return {
            loading: true,
            showFilters: false,
            query: null,
            status: { label: 'All', value: null },
            dates: {
                rangeStart: null,
                rangeEnd: null,
                filterStart: null,
                filterEnd: null,
            }
        }
    },

    computed: {
        ...mapState('jobs', ['jobs', 'count', 'page', 'filters']),

        showDateFilter() {
            // Make sure range is at least 3 months
            const start = moment(this.dates.rangeStart).startOf('month').add(2, 'months');
            const end = moment(this.dates.rangeEnd).startOf('month');
            return start.isSameOrBefore(end);
        }
    },

    created() {
        this.fetchFilteredSet();
    },

    mounted() {
        EventBus.$on('new-job-added', this.resetFilters);
        EventBus.$on('new-job-modal', this.openModal);

        // this.openModal();
    },

    destroyed() {
        EventBus.$off('new-job-added', this.resetFilters);
        EventBus.$off('new-job-modal', this.openModal);
    },

    methods: {
        ...mapActions('jobs', ['fetchJobs']),

        async fetchFilteredSet({ q = this.query, dateStart = this.dates.filterStart, dateEnd = this.dates.filterEnd, status = this.status.value, page = 1 } = {}, isDate = false) {
            this.loading = true;

            const { filters: { date } } = await this.fetchJobs({ q, status, page, dateStart, dateEnd });

            if (!isDate && page === 1) {
                const minFilter = moment(date.start).format('YYYY-MM-DD');
                const maxFilter = moment(date.end).format('YYYY-MM-DD');
                this.dates.rangeStart = minFilter;
                this.dates.rangeEnd = maxFilter;
            }

            this.loading = false;
        },

        changePage(page) {
            this.fetchFilteredSet({ page });
        },

        resetFilters() {
            this.query = null;
            this.status = { label: 'All', value: null };
            this.dates = {
                rangeStart: null,
                rangeEnd: null,
                filterStart: null,
                filterEnd: null,
            };
            this.fetchFilteredSet();
        },

        openModal() {
            this.$refs.modal.open();
        }
    }
};
</script>

<style lang="scss" scoped>
    .flex-1 {
        flex: 1
    }

    .no-results {
        margin-top: 6em;
        text-align: center;
    }

    .search-filter__toggle {
        flex: 0 0 auto;
    }

    .search-filter__section-bottom {
        justify-content: space-between;

        &::after {
            display: none;
        }
    }
</style>
